import { AggregationFunction } from "@multiweb/clients/dataReader";
import {
    AGGREGATION_FUNCTION_SELECTED,
    CLEAR_AGGREGATION_ROW,
    CLOSE_BUTTON_ACTIVATED,
    COLUMN_ADDED,
    COLUMN_DEFINITIONS_CHANGED,
    COLUMN_REMOVED,
    COLUMN_WIDTH_CHANGED,
    FILTER_CHANGED,
    INIT_AGGREGATION_ROW,
    NUMBER_OF_FROZEN_COLUMNS_CHANGED,
    ORDER_OF_COLUMNS_CHANGED,
    RELOAD_AGGREGATION_RESULTS,
    SET_AGGREGATION_RESULTS,
} from "./constants";
import {
    AggregationRowKey,
    ColumnDefinition,
    ColumnKey,
    Filter,
    FormattedAggregationResults,
    IAggregationFunctionSelectedAction,
    IClearAggregationRowAction,
    ICloseButtonActivatedAction,
    IColumnAddedAction,
    IColumnDefinitionsChangedAction,
    IColumnRemovedAction,
    IColumnWidthChangedAction,
    IFilterChangedAction,
    IInitAggregationRowAction,
    INumberOfFrozenColumnsChangedAction,
    IOrderOfColumnsChangedAction,
    IReloadAggregationResultsAction,
    ISetAggregationResultsAction,
} from "./types";

/**
 * Inicializuje agregační řádek se zadaným klíčem a sloupci.
 *
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param className Jméno třídy, nad kterou se provádí agregace jejích atributů.
 * @param columnDefinitions Definice sloupců.
 * @param orderOfColumns Pořadí sloupců specifikované pomocí pořadí jejich klíčů v tomto poli.
 * @param numberOfFrozenColumns Počet sloupců zmrazených pro horizontální scrollování.
 * @param filter Počáteční hodnota filtru pro omezení agregovaných záznamů.
 */
export const createInitAggregationRowAction = (
    aggregationRowKey: AggregationRowKey,
    className: string,
    columnDefinitions: ReadonlyArray<ColumnDefinition>,
    orderOfColumns: ReadonlyArray<ColumnKey>,
    numberOfFrozenColumns: number,
    filter: Filter,
): IInitAggregationRowAction => ({
    type: INIT_AGGREGATION_ROW,
    payload: {
        className,
        columnDefinitions,
        orderOfColumns,
        numberOfFrozenColumns,
        filter,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Odstraní všechna data pro agregační řádek se zadaným klíčem.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 */
export const createClearAggregationRowAction = (aggregationRowKey: AggregationRowKey): IClearAggregationRowAction => ({
    type: CLEAR_AGGREGATION_ROW,
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce signalizující výběr agregační funkce.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param columnKey Klíč sloupce, pro který byla vybrána agregační funkce.
 * @param aggregationFunction Vybraná agregační funkce.
 */
export const createAggregationFunctionSelectedAction = (
    aggregationRowKey: AggregationRowKey,
    columnKey: ColumnKey,
    aggregationFunction: AggregationFunction | undefined,
): IAggregationFunctionSelectedAction => ({
    type: AGGREGATION_FUNCTION_SELECTED,
    payload: {
        columnKey,
        aggregationFunction,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Nastaví výsledky agregačních funkcí do odpovídajících sloupců.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param results Pole výsledků agregačních funkcí pro jednotlivé sloupce
 */
export const createSetAggregationResultsAction = (aggregationRowKey: AggregationRowKey, results: FormattedAggregationResults): ISetAggregationResultsAction => ({
    type: SET_AGGREGATION_RESULTS,
    payload: {
        results,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce signalizující změnu šířky sloupce.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param columnKey Klíč sloupce, u kterého se změnila šířka.
 * @param width Šířka sloupce v pixelech.
 */
export const createColumnWidthChangedAction = (aggregationRowKey: AggregationRowKey, columnKey: ColumnKey, width: number): IColumnWidthChangedAction => ({
    type: COLUMN_WIDTH_CHANGED,
    payload: {
        columnKey,
        width,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce signalizující změnu počtu sloupců se zmrazeným horizontálním scrollováním.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param numberOfFrozenColumns Počet sloupců se zmrazeným horizontálním scrollováním.
 */
export const createNumberOfFrozenColumnsChangedAction = (aggregationRowKey: AggregationRowKey, numberOfFrozenColumns: number): INumberOfFrozenColumnsChangedAction => ({
    type: NUMBER_OF_FROZEN_COLUMNS_CHANGED,
    payload: {
        numberOfFrozenColumns,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce signalizující změnu pořadí sloupců.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param orderOfColumns Pole s klíči sloupců v novém pořadí.
 */
export const createOrderOfColumnsChangedAction = (aggregationRowKey: AggregationRowKey, orderOfColumns: ReadonlyArray<ColumnKey>): IOrderOfColumnsChangedAction => ({
    type: ORDER_OF_COLUMNS_CHANGED,
    payload: {
        orderOfColumns,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce signalizující změnu definic sloupců. Nemění zvolené agregační funkce a jejich výsledky pokud je zachován typ sloupce.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param columnDefinitions Definice sloupců.
 * @param orderOfColumns Pořadí sloupců specifikované pomocí pořadí jejich klíčů v tomto poli.
 * @param numberOfFrozenColumns Počet sloupců zmrazených pro horizontální scrollování.
 */
export const createColumnDefinitionsChangedAction = (
    aggregationRowKey: AggregationRowKey,
    columnDefinitions: ReadonlyArray<ColumnDefinition>,
    orderOfColumns: ReadonlyArray<ColumnKey>,
    numberOfFrozenColumns: number,
): IColumnDefinitionsChangedAction => ({
    type: COLUMN_DEFINITIONS_CHANGED,
    payload: {
        columnDefinitions,
        orderOfColumns,
        numberOfFrozenColumns,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce signalizující přidání sloupce.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param column Definice nového sloupce.
 */
export const createColumnAddedAction = (aggregationRowKey: AggregationRowKey, column: ColumnDefinition): IColumnAddedAction => ({
    type: COLUMN_ADDED,
    payload: {
        column,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce signalizující odebrání sloupce.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @param columnKey Klíč odebraného sloupce.
 */
export const createColumnRemovedAction = (aggregationRowKey: AggregationRowKey, columnKey: ColumnKey): IColumnRemovedAction => ({
    type: COLUMN_REMOVED,
    payload: {
        columnKey,
    },
    meta: {
        aggregationRowKey,
    },
});

/**
 * Provede načtení hodnot pro všechny sloupce, které mají vybranou agregační funkcí.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 */
export const createReloadAggregationResultsAction = (aggregationRowKey: AggregationRowKey): IReloadAggregationResultsAction => ({
    type: RELOAD_AGGREGATION_RESULTS,
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce indikující aktivaci tlačítka pro zavření agregačního řádku.
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 */
export const createCloseButtonActivatedAction = (aggregationRowKey: AggregationRowKey): ICloseButtonActivatedAction => ({
    type: CLOSE_BUTTON_ACTIVATED,
    meta: {
        aggregationRowKey,
    },
});

/**
 * Akce signalizující změnu filtru pro záznamy v tabulce.
 * @param aggregationRowKey Klíč agregačního řádku, pro nějž se změnil filtr.
 * @param filter Nové hodnoty filtru.
 */
export const createFilterChangedAction = (aggregationRowKey: AggregationRowKey, filter: Filter): IFilterChangedAction => ({
    type: FILTER_CHANGED,
    payload: {
        filter,
    },
    meta: {
        aggregationRowKey,
    },
});
